/**
 * 全アセット（.vrma）の復号。
 *
 * 実体は AES-256-GCM で暗号化して S3 に置き（暗号文しか存在しない）、暗号化はサーバーサイドで
 * 行う。復号鍵はフロントのビルド定数（GitHub Secrets → Actions → Vite の
 * VITE_OFFICIAL_ASSET_KEY）として埋め込む。API は鍵を一切返さない。
 * フロントはブラウザでのプレビュー再生のための復号のみを行う（アップロード時の暗号化はしない）。
 *
 * 暗号化フォーマット（backend / シードスクリプトと共有）:
 *   アルゴリズム: AES-256-GCM
 *   鍵: 32バイト。base64 文字列として VITE_OFFICIAL_ASSET_KEY に格納
 *   S3 に置くバイト列: [IV 12バイト][暗号文 + GCM認証タグ 16バイト]（IV は先頭に連結。DBには持たない）
 *
 * `crypto.subtle` は https（または localhost）でのみ利用可能な点に注意。
 */

const KEY_B64 = import.meta.env.VITE_OFFICIAL_ASSET_KEY;
const IV_LENGTH = 12;

/** 鍵が設定されているか（環境変数の存在チェックのみ、フォーマットの妥当性は見ない）。 */
export function isProtectedAssetKeyConfigured(): boolean {
  return !!KEY_B64;
}

function base64ToBytes(b64: string) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

let cachedKey: Promise<CryptoKey> | null = null;

/** VITE_OFFICIAL_ASSET_KEY を AES-GCM 鍵としてインポートする（結果はプロセス内でキャッシュ）。 */
function importKey(): Promise<CryptoKey> {
  if (!KEY_B64) {
    throw new Error(
      '保護アセット用の鍵（VITE_OFFICIAL_ASSET_KEY）が設定されていません。ビルド時に環境変数を設定してください。',
    );
  }
  if (!cachedKey) {
    cachedKey = crypto.subtle.importKey('raw', base64ToBytes(KEY_B64), 'AES-GCM', false, [
      'encrypt',
      'decrypt',
    ]);
  }
  return cachedKey;
}

// url -> 復号済み blob URL。同じ url に対する複数回の呼び出しでも同じ blob URL を返す。
const cache = new Map<string, Promise<string>>();

/**
 * 暗号化された .vrma を url から取得して復号し、blob URL を返す。
 * 結果は url をキーにキャッシュし、同じアニメーションを何度開いても再利用する
 * （URL.revokeObjectURL は呼び出し側で行わない）。
 */
export function decryptToBlobUrl(url: string): Promise<string> {
  const cached = cache.get(url);
  if (cached) return cached;
  const p = (async () => {
    const key = await importKey();
    const res = await fetch(url);
    if (!res.ok) throw new Error(`保護アセットの取得に失敗しました (${res.status})`);
    const buf = await res.arrayBuffer();
    if (buf.byteLength <= IV_LENGTH) {
      throw new Error('保護アセットのデータが不正です。');
    }
    const iv = new Uint8Array(buf, 0, IV_LENGTH);
    const cipher = buf.slice(IV_LENGTH);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    const blob = new Blob([plain], { type: 'application/octet-stream' });
    return URL.createObjectURL(blob);
  })();
  cache.set(url, p);
  // 復号に失敗した場合は次回リトライできるようキャッシュから外す。
  p.catch(() => cache.delete(url));
  return p;
}
